import { getTodoTaskHistory } from "../../services/todoTask";
import React, { useEffect, useState } from "react";
import {
    Grid,
    Typography,
    Stack,
    Avatar,
    DialogContent,
    DialogTitle,
    IconButton,
    Box,
    Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import moment from "moment";

const TodoTaskHistory = ({ selectedData, setOpenHistoryDrawer, users = [] }) => {
    const [historyData, setHistoryData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchHistory = async () => {
            if (selectedData?._id) {
                setLoading(true);
                try {
                    const res = await getTodoTaskHistory(selectedData._id);
                    console.log("History API Response:", res); // Debug log
                    console.log("History API Response:", res); // Debug log
                    if (res?.data) {
                        setHistoryData(res.data);
                    } else {
                        console.log("Checking structure:", res);
                    }
                } catch (error) {
                    console.error("Error fetching history:", error);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchHistory();
    }, [selectedData]);

    const formatChanges = (text) => {
        if (!text) return text;

        // Clean up the text if it contains "by [Name]" since we display the name separately now
        // This is optional, but keeps the message clean. 
        // For now, highlighting keywords is good enough.
        return text
            .replace(
                /(Created|Task|was|changed|from|to|by|reassigned|updated|added|Status|Completion|date|Description|Project|File|Assigned|Priority|Level)/gi,
                "<strong>$&</strong>"
            )
            .replace(/\b([A-Z][a-z]+ [A-Z][a-z]+)\b/g, "<strong>$&</strong>");
    };

    const getUserDetails = (createdBy) => {
        if (!createdBy) return { name: "System", avatar: null };

        // If createdBy is populated (is an object), use it
        if (typeof createdBy === 'object' && createdBy.firstName) {
            return {
                name: `${createdBy.firstName} ${createdBy.lastName}`,
                avatar: null
            };
        }

        // Fallback checks
        if (typeof createdBy === 'string') {
            const user = users.find((u) => u._id === createdBy);
            if (user) {
                return {
                    name: `${user.firstName} ${user.lastName}`,
                    avatar: user.avatar || null
                };
            }
        }
        return { name: "Unknown User", avatar: null };
    };

    // Fallback regex if ID not found (for old data support if needed)
    const getUserNameFromChanges = (changes) => {
        const createdByMatch = changes?.match(/by (.*?)\.?$/);
        if (createdByMatch && createdByMatch[1]) {
            return createdByMatch[1].trim();
        }
        return "Unknown";
    };

    const getStatusChange = (changes) => {
        // Regex to match "Status changed from X to Y" handles optional "by..."
        const statusMatch = changes.match(/Status changed from (.*?) to (.*?)(?: by|$)/);
        if (statusMatch) {
            return { from: statusMatch[1].trim(), to: statusMatch[2].trim() };
        }
        return null;
    };

    const sortedHistory = historyData
        ?.slice()
        ?.sort((a, b) => new Date(b.date) - new Date(a.date))
        ?.filter(task => {
            const taskDateString = task?.date;
            return taskDateString && !isNaN(new Date(taskDateString).getTime());
        });

    return (
        <>
            <DialogTitle sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 2, sm: 3 } }}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    borderBottom="1px solid"
                    borderColor="divider"
                    pb={1}
                >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <HistoryIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
                            TodoTask History
                        </Typography>
                        <Chip
                            label={`${sortedHistory?.length || 0} entries`}
                            size="small"
                            variant="outlined"
                            sx={{
                                display: { xs: "none", sm: "inline-flex" },
                                height: 20,
                                fontSize: "0.75rem"
                            }}
                        />
                    </Stack>
                    <IconButton onClick={() => setOpenHistoryDrawer(false)} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
                {/* Mobile only entries count */}
                <Box sx={{ display: { xs: "block", sm: "none" }, mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                        {sortedHistory?.length || 0} entries found
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    {!sortedHistory || sortedHistory.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <HistoryIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
                            <Typography variant="body1" color="text.secondary">
                                No history available
                            </Typography>
                        </Box>
                    ) : (
                        <Stack spacing={2}>
                            {sortedHistory.map((task, index) => {
                                let userDetails = getUserDetails(task.createdBy);

                                if (userDetails.name === "Unknown User" || userDetails.name === "System") {
                                    const regexName = getUserNameFromChanges(task.changes);
                                    if (regexName !== "Unknown") {
                                        userDetails.name = regexName;
                                    }
                                }

                                const statusChange = getStatusChange(task.changes);
                                const isCreation = task.changes?.includes('created');

                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            p: { xs: 1.5, sm: 2 },
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            backgroundColor: 'background.paper',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                backgroundColor: '#fafafa'
                                            }
                                        }}
                                    >
                                        <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="flex-start">
                                            <Avatar
                                                alt={userDetails.name}
                                                src={task?.avatarUrl}
                                                sx={{
                                                    backgroundColor: 'action.hover',
                                                    color: 'text.primary',
                                                    height: { xs: 32, sm: 40 },
                                                    width: { xs: 32, sm: 40 },
                                                    fontSize: { xs: "12px", sm: "14px" }
                                                }}
                                            >
                                                {userDetails.name?.charAt(0).toUpperCase()}
                                            </Avatar>

                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Stack
                                                    direction={{ xs: "column", sm: "row" }}
                                                    justifyContent="space-between"
                                                    alignItems={{ xs: "flex-start", sm: "flex-start" }}
                                                    spacing={{ xs: 0.5, sm: 0 }}
                                                    sx={{ mb: 1 }}
                                                >
                                                    <Box>
                                                        <Typography variant="subtitle2" fontWeight="600" sx={{ lineHeight: 1.2 }}>
                                                            {userDetails.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                                                            {moment.utc(task.date).format("MMM Do YYYY, h:mm a")}
                                                        </Typography>
                                                    </Box>
                                                    {isCreation && (
                                                        <Chip
                                                            label="Created"
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                            sx={{
                                                                height: 20,
                                                                fontSize: "0.7rem",
                                                                mt: { xs: 0.5, sm: 0 },
                                                                alignSelf: { xs: 'flex-start', sm: 'auto' }
                                                            }}
                                                        />
                                                    )}
                                                </Stack>

                                                {statusChange && (
                                                    <Box sx={{ mb: 1.5 }}>
                                                        <Stack
                                                            direction="row"
                                                            alignItems="center"
                                                            spacing={1}
                                                            flexWrap="wrap"
                                                            gap={0.5}
                                                        >
                                                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                                                Status:
                                                            </Typography>
                                                            <Chip
                                                                label={statusChange.from}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ fontSize: '11px', height: 20 }}
                                                            />
                                                            <Typography variant="caption" color="text.secondary">
                                                                →
                                                            </Typography>
                                                            <Chip
                                                                label={statusChange.to}
                                                                size="small"
                                                                color="primary"
                                                                sx={{ fontSize: '11px', height: 20 }}
                                                            />
                                                        </Stack>
                                                    </Box>
                                                )}

                                                <Box
                                                    sx={{
                                                        color: 'text.primary',
                                                        fontSize: { xs: "13px", sm: "14px" },
                                                        lineHeight: 1.5,
                                                        wordBreak: "break-word",
                                                        '& strong': {
                                                            fontWeight: 600,
                                                            color: 'text.primary'
                                                        },
                                                        '& span': {
                                                            display: 'inline-block'
                                                        }
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: formatChanges(task.changes),
                                                    }}
                                                />
                                            </Box>
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </Box>
            </DialogContent>
        </>
    );
};

export default TodoTaskHistory;
